import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFirebase } from "../../contexts/FirebaseContext";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const Dashboard = () => {
	const { t } = useTranslation();
	const { isAuthenticated, readCollection } = useFirebase();

	const [participants, setParticipants] = useState([]);
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		loadParticipants();
	}, []);

	const loadParticipants = async () => {
		try {
			setLoading(true);

			const data = await readCollection("iqmRegistrations", "name");

			setParticipants(data);
		} catch (err) {
			console.error(err);
			alert("Failed to load participants.");
		} finally {
			setLoading(false);
		}
	};

	const filteredParticipants = useMemo(() => {
		const keyword = search.toLowerCase().trim();

		return participants.filter((participant) => {
			return participant.name?.toLowerCase().includes(keyword) || participant.tel?.includes(keyword);
		});
	}, [participants, search]);

	const exportExcel = () => {
		const data = filteredParticipants.map((participant, index) => ({
			No: index + 1,
			Nama: participant.name,
			Tel: participant.tel,
		}));

		const worksheet = XLSX.utils.json_to_sheet(data);

		const workbook = XLSX.utils.book_new();

		XLSX.utils.book_append_sheet(workbook, worksheet, "Participants");

		const excelBuffer = XLSX.write(workbook, {
			bookType: "xlsx",
			type: "array",
		});

		saveAs(new Blob([excelBuffer]), "Participants.xlsx");
	};

	return (
		<div className="flex p-4 w-full justify-center items-center">
			<div className="max-w-3xl w-full">
				<span className="text-2xl font-bold text-violet-950">{t("participants.participants")}</span>

				<div className="flex flex-row">
					<div className="flex flex-col flex-1 p-2 bg-green-600">
						<span className="text-xs text-center text-green-50">{t("participants.totalParticipants")}</span>
						<span className="text-2xl text-center font-bold text-green-50">{filteredParticipants.length}</span>
					</div>

					<div className="flex flex-col flex-1 p-0">
						<button
							onClick={exportExcel}
							className="p-1 flex-1 bg-blue-600 hover:bg-blue-500 focus:bg-blue-500 active:bg-blue-500 text-violet-50 text-sm">
							{t("participants.exportExcel")}
						</button>

						<button
							onClick={loadParticipants}
							className="p-1 flex-1 bg-yellow-600 hover:bg-yellow-500 focus:bg-yellow-500 active:bg-yellow-500 text-violet-50 text-sm">
							{t("participants.refresh")}
						</button>
					</div>
				</div>

				<div className="flex flex-row gap-2">
					<input
						type="text"
						placeholder="Cari nama atau telefon..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="w-full p-1 bg-violet-50 border hover:border-violet-950 focus:border-violet-950 active:border-violet-950 rounded-none outline-none"
					/>
				</div>

				{loading ?
					<p>{t("status.loading")}</p>
				:	<table
						border="1"
						cellPadding="4"
						cellSpacing="0"
						width="100%">
						<thead className="bg-violet-950 text-violet-50">
							<tr>
								<th className="w-10">#</th>
								<th>{t("participants.name")}</th>
								<th>{t("participants.phone")}</th>
							</tr>
						</thead>

						<tbody>
							{filteredParticipants.length === 0 ?
								<tr>
									<td
										colSpan="3"
										align="center">
										{t("participants.noParticipantFound")}
									</td>
								</tr>
							:	filteredParticipants.map((participant, index) => (
									<tr
										key={participant.id}
										className={`${index % 2 === 0 ? "bg-violet-50" : "bg-white"} hover:bg-violet-100 focus:bg-violet-100 active:bg-violet-100`}>
										<td>{index + 1}</td>
										<td>{participant.name}</td>
										<td>{participant.tel}</td>
									</tr>
								))
							}
						</tbody>
					</table>
				}
			</div>
		</div>
	);
};

export default Dashboard;
